import { CareerTrack, TrackCode } from './careerTrack';

export const TIERS = {
  SEMISKILLED: 'Semi-skilled',
  TRADE: 'Trades/Technical',
  MID: 'Middle-Tier Professional',
  UPPER: 'Upper-Tier Professional',
  TOP: 'Top-Tier Professional',
  ENTREPRENEURSHIP: 'Entrepreneurship',
} as const;

export const TRACK_CODE_TO_NAME: Record<TrackCode, Tier> = {
  'semi-skilled': TIERS.SEMISKILLED,
  trades: TIERS.TRADE,
  'middle-tier': TIERS.MID,
  'upper-tier': TIERS.UPPER,
  'top-tier': TIERS.TOP,
  entrepreneurship: TIERS.ENTREPRENEURSHIP,
};

export const TIER_TO_TRACK_CODE: Record<Tier, TrackCode> = {
  [TIERS.SEMISKILLED]: 'semi-skilled',
  [TIERS.TRADE]: 'trades',
  [TIERS.MID]: 'middle-tier',
  [TIERS.UPPER]: 'upper-tier',
  [TIERS.TOP]: 'top-tier',
  [TIERS.ENTREPRENEURSHIP]: 'entrepreneurship',
};

// Mapping from Tier to the old CareerTrack name format for backward compatibility
export const TIER_TO_CAREER_TRACK_NAME: Record<Tier, CareerTrack['name']> = {
  [TIERS.SEMISKILLED]: 'Semi-skilled',
  [TIERS.TRADE]: 'Trades/Technical',
  [TIERS.MID]: 'Middle-Tier',
  [TIERS.UPPER]: 'Upper-Tier',
  [TIERS.TOP]: 'Top-Tier',
  [TIERS.ENTREPRENEURSHIP]: 'Entrepreneurship',
};

export type Tier = (typeof TIERS)[keyof typeof TIERS];

export type CareerPathScoreSection = 'TOP' | 'MIDDLE' | 'LOW';

export type CareerCategory = Readonly<{
  id: number;
  name: string;
  slug: CareerCategorySlug;
  short_name: string;
}>;

// career category slugs from the database
export type CareerCategorySlug =
  | 'agriculture'
  | 'arts'
  | 'business'
  | 'education'
  | 'law'
  | 'patient-care'
  | 'stem'
  | 'social-sector'
  | 'trades';

export type CareerPathGrowth = 'Negative' | 'Stable' | 'Low' | 'Medium' | 'High';

export type CareerPathAIDisruptionLevel = 'Very Significant' | 'Significant' | 'Other' | 'Modest';

export type CareerTrackDistribution = {
  [K in Exclude<keyof typeof TIERS, 'ENTREPRENEURSHIP'>]: string;
};

export type EarlyCareerHires = Readonly<{
  skilled_technical: number;
  professional: number;
}>;

export type CaregiverFlexibilityRating = 'High' | 'Medium' | 'Low';

type CareerPathCaregiverFlexibility = Readonly<{
  caregiver_flexibility_score?: CaregiverFlexibilityRating;
  caregiver_flexibility_part_time_flexible_scheduling?: CaregiverFlexibilityRating;
  caregiver_flexibility_ease_of_reentry_after_gap?: CaregiverFlexibilityRating;
  caregiver_flexibility_licensure_credential_stability?: CaregiverFlexibilityRating;
  caregiver_flexibility_cultural_support_for_caregivers?: CaregiverFlexibilityRating;
  caregiver_flexibility_skill_half_life_change_velocity?: CaregiverFlexibilityRating;
  caregiver_flexibility_remote_location_flexibility?: CaregiverFlexibilityRating;
  caregiver_flexibility_career_progression_flexibility?: CaregiverFlexibilityRating;
  caregiver_flexibility_synthesis?: string;
}>;

export const AI_IMPACT_AUTOMATION_RISK = {
  Level_1: 'Unlikely',
  Level_2: 'Partial',
  Level_3: 'Significant',
  Level_4: 'High',
} as const;

export const AI_IMPACT_INTEGRATION_INTENSITY = {
  Level_1: 'Minimal',
  Level_2: 'Supportive',
  Level_3: 'Embedded',
  Level_4: 'Central',
} as const;

export const AI_IMPACT_ROLE_REINVENTION_NEED = {
  Level_1: 'Stable',
  Level_2: 'Adapting',
  Level_3: 'Reconfiguring',
  Level_4: 'Transforming',
} as const;

export const AI_IMPACT_LEVERAGE_UPSIDE = {
  Level_1: 'Low',
  Level_2: 'Helpful',
  Level_3: 'Differentiating',
  Level_4: 'Transformative',
} as const;

export const AI_IMPACT_HUMAN_DIFFERENTIATOR_INDEX = {
  Level_1: 'Replaceable',
  Level_2: 'Useful',
  Level_3: 'Enhancing',
  Level_4: 'Essential',
} as const;

// Create types from the const assertions
export type AiImpactAutomationRisk =
  (typeof AI_IMPACT_AUTOMATION_RISK)[keyof typeof AI_IMPACT_AUTOMATION_RISK];
export type AiImpactIntegrationIntensity =
  (typeof AI_IMPACT_INTEGRATION_INTENSITY)[keyof typeof AI_IMPACT_INTEGRATION_INTENSITY];
export type AiImpactRoleReinventionNeed =
  (typeof AI_IMPACT_ROLE_REINVENTION_NEED)[keyof typeof AI_IMPACT_ROLE_REINVENTION_NEED];
export type AiImpactLeverageUpside =
  (typeof AI_IMPACT_LEVERAGE_UPSIDE)[keyof typeof AI_IMPACT_LEVERAGE_UPSIDE];
export type AiImpactHumanDifferentiatorIndex =
  (typeof AI_IMPACT_HUMAN_DIFFERENTIATOR_INDEX)[keyof typeof AI_IMPACT_HUMAN_DIFFERENTIATOR_INDEX];

export type CareerPathAiImpact = Readonly<{
  ai_impact_automation_risk?: AiImpactAutomationRisk;
  ai_impact_integration_intensity?: AiImpactIntegrationIntensity;
  ai_impact_role_reinvention_need?: AiImpactRoleReinventionNeed;
  ai_impact_leverage_upside?: AiImpactLeverageUpside;
  ai_impact_human_differentiator_index?: AiImpactHumanDifferentiatorIndex;
  ai_impact_five_year_impact_summary?: string;
}>;

export type CareerPath = Readonly<{
  id: number;
  code?: string;
  career_category_id: number;
  career_category_img?: string;
  career_category?: CareerCategory;
  media_url?: string;
  name: string;
  description: string;
  insight: string;
  early_career_hires_per_year: EarlyCareerHires;
  growth: CareerPathGrowth;
  ai_disruption: CareerPathAIDisruptionLevel;
  career_track_distribution: CareerTrackDistribution;
  opportunities?: string;
  profiles?: string;
  employers?: string;
  askLubavButtonResponse?: string;
  caregiver_flexibility: CareerPathCaregiverFlexibility;
  ai_impact: CareerPathAiImpact;
}>;

/** The career path model is stored in a way that it needs minor transforming */
export type CareerPathResponse = Readonly<
  {
    id: number;
    career_category_id: number;
    name: string;
    description: string;
    insight: string;
    early_career_hires_per_year_skilled_technical: number;
    early_career_hires_per_year_professional: number;
    growth: CareerPathGrowth;
    ai_disruption: CareerPathAIDisruptionLevel;
    career_track_distribution_semi_skilled: string;
    career_track_distribution_trade: string;
    career_track_distribution_mid: string;
    career_track_distribution_upper: string;
    career_track_distribution_top: string;
    career_category?: CareerCategory;
    minimum_track_name?: Tier;
    askLubavButtonResponse?: string;
    media_url?: string;
  } & CareerPathCaregiverFlexibility &
    CareerPathAiImpact
>;

export const mapCareerPathResponseToCareerPath = (career_path: CareerPathResponse): CareerPath => {
  return {
    id: career_path.id,
    career_category_id: career_path.career_category_id,
    career_category: career_path.career_category,
    name: career_path.name,
    description: career_path.description,
    insight: career_path.insight,
    early_career_hires_per_year: {
      skilled_technical: career_path.early_career_hires_per_year_skilled_technical,
      professional: career_path.early_career_hires_per_year_professional,
    },
    growth: career_path.growth,
    ai_disruption: career_path.ai_disruption,
    career_track_distribution: {
      SEMISKILLED: career_path.career_track_distribution_semi_skilled,
      TRADE: career_path.career_track_distribution_trade,
      MID: career_path.career_track_distribution_mid,
      UPPER: career_path.career_track_distribution_upper,
      TOP: career_path.career_track_distribution_top,
    },
    askLubavButtonResponse: career_path.askLubavButtonResponse,
    media_url: career_path.media_url,
    caregiver_flexibility: {
      caregiver_flexibility_score: career_path.caregiver_flexibility_score,
      caregiver_flexibility_part_time_flexible_scheduling:
        career_path.caregiver_flexibility_part_time_flexible_scheduling,
      caregiver_flexibility_ease_of_reentry_after_gap:
        career_path.caregiver_flexibility_ease_of_reentry_after_gap,
      caregiver_flexibility_licensure_credential_stability:
        career_path.caregiver_flexibility_licensure_credential_stability,
      caregiver_flexibility_cultural_support_for_caregivers:
        career_path.caregiver_flexibility_cultural_support_for_caregivers,
      caregiver_flexibility_skill_half_life_change_velocity:
        career_path.caregiver_flexibility_skill_half_life_change_velocity,
      caregiver_flexibility_remote_location_flexibility:
        career_path.caregiver_flexibility_remote_location_flexibility,
      caregiver_flexibility_career_progression_flexibility:
        career_path.caregiver_flexibility_career_progression_flexibility,
      caregiver_flexibility_synthesis: career_path.caregiver_flexibility_synthesis,
    },
    ai_impact: {
      ai_impact_automation_risk: career_path.ai_impact_automation_risk,
      ai_impact_integration_intensity: career_path.ai_impact_integration_intensity,
      ai_impact_role_reinvention_need: career_path.ai_impact_role_reinvention_need,
      ai_impact_leverage_upside: career_path.ai_impact_leverage_upside,
      ai_impact_human_differentiator_index: career_path.ai_impact_human_differentiator_index,
      ai_impact_five_year_impact_summary: career_path.ai_impact_five_year_impact_summary,
    },
  };
};

export type CareerPathCompensation = Readonly<{
  id: number;
  career_path_id: number;
  career_track_id: number;
  entry_level_min: number | null;
  entry_level_max: number | null;
  mid_career_min: number | null;
  mid_career_max: number | null;
  late_career_min: number | null;
  late_career_max: number | null;
}>;

export type CareerPathSampleJob = Readonly<{
  id: number;
  name: string;
}>;

export type CareerPathSampleJobs = Readonly<{
  career_path_id: number;
  tiers: Partial<Record<CareerTrack['name'], CareerPathSampleJob[]>>;
}>;

export type CareerPathFitLevel = 'Strong Fit' | 'Medium Fit' | 'Low Fit';

export type CareerPathAIDisruption = Readonly<{
  automation_risk: string;
  ai_integration_intensity: string;
  role_reinvention_need: string;
  ai_leverage_upside: string;
  human_differentiator_index: string;
  career_path_id: number;
}>;

export type CareerQuestionFacts = Readonly<{
  type: 'TEXT' | 'LINK';
  value: string;
  sequence: number;
  url?: string;
}>;
